import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, ArrowRight, ChevronRight, Fingerprint, Award, Building2, FileSignature } from "lucide-react";
import { API_BASE } from "../../lib/api";
import { VideoPreview } from "../common/VideoLightbox";
import { useHeroVideoConfig } from "../../lib/useHeroVideoConfig";

/* ---------- Platform Pulse card (right column of hero) ---------- */
function StatTile({ label, value, testId }) {
  return (
    <div className="p-3 rounded-lg border border-navy/[0.08] bg-white" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#8A6600]">{label}</div>
      <div className="font-heading text-xl mt-0.5" style={{ color: "#0F3D2E" }}>{value}</div>
    </div>
  );
}

function PlatformPulseCard() {
  const [pulse, setPulse] = useState({
    labels: {
      companies_evaluated: "Companies Evaluated",
      institutions_on_file: "Institutions on File",
      term_sheets: "Term Sheets Facilitated",
      median_trust: "Median Trust Score",
    },
    stats: { companies_evaluated: 4820, institutions_on_file: 380, term_sheets: 214, median_trust: 78 },
    featured: [],
  });  useEffect(() => {
    let mounted = true;
    fetch(`${API_BASE}/public/hero-pulse`)
      .then((r) => r.json())
      .then((d) => { if (mounted && d) setPulse(d); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const s = pulse.stats || {};
  const l = pulse.labels || {};
  const featured = pulse.featured || [];
  const fmtAsk = (n) => {
    if (!n) return "—";
    if (n >= 1e7) return `₹${(n / 1e7).toFixed(0)} Cr`;
    if (n >= 1e5) return `₹${(n / 1e5).toFixed(0)} L`;
    return `₹${n}`;
  };
  const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");

  return (
    <div
      className="w-full rounded-2xl border border-white/20 shadow-2xl p-6 bg-white/95 backdrop-blur-md"
      style={{ color: "#0F3D2E" }}
      data-testid="hero-snapshot-card"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#8A6600] flex items-center gap-1.5">
          <ShieldCheck size={11} /> PLATFORM PULSE
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-700">
          LIVE
        </span>
      </div>

      {/* Stat tiles (inflated, admin-controlled) */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <StatTile label={l.companies_evaluated} value={fmtNum(s.companies_evaluated)} testId="pulse-stat-companies" />
        <StatTile label={l.institutions_on_file} value={fmtNum(s.institutions_on_file)} testId="pulse-stat-institutions" />
        <StatTile label={l.term_sheets} value={fmtNum(s.term_sheets)} testId="pulse-stat-termsheets" />
        <StatTile label={l.median_trust} value={`${s.median_trust}/100`} testId="pulse-stat-trust" />
      </div>

      {/* Featured verified opportunities — curated by admin */}
      <div className="space-y-2.5" data-testid="pulse-featured-list">
        {featured.length === 0 && (
          <div className="text-xs text-ink-muted italic">Curated opportunities appear here.</div>
        )}
        {featured.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-3" data-testid={`pulse-featured-${t.id}`}>
            <div className="min-w-0">
              <div className="font-editorial italic text-[17px] leading-tight truncate" style={{ color: "#0F3D2E" }}>{t.startup_name}</div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mt-0.5">
                {t.sector || "—"} · {t.business_stage || "—"}
                {t.funding_ask ? ` · ${fmtAsk(t.funding_ask)}` : ""}
              </div>
            </div>
            <span className="shrink-0 text-[11px] font-mono px-2 py-0.5 rounded bg-gold/15 border border-gold/40 text-[#8A6600]">
              {t.trust_score ?? "—"}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-5 pt-4 border-t border-navy/[0.08] flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-ink-muted">
          <Lock size={10} className="inline mr-1" /> Locked · Investor Approval Required
        </span>
        <Link to="/marketplace" className="text-[11px] font-heading text-[#8A6600] hover:underline" data-testid="pulse-preview-link">
          Preview →
        </Link>
      </div>
    </div>
  );
}

/* ================== Verified Capital Access pillars ================== */
/* Sits right under the hero — 4 columns highlighting the proprietary
 * VenturaZ moat. Passport + Trust Score are marked as proprietary. */
function PillarStrip() {
  const pillars = [
    { icon: Fingerprint, label: "VenturaZ Passport", desc: "One verified profile · re-used across every raise & deal room.", proprietary: true, testId: "pillar-passport" },
    { icon: Award, label: "VenturaZ Trust Score", desc: "0–100 score built from 40+ signals · no other platform has it.", proprietary: true, testId: "pillar-trust" },
    { icon: Building2, label: "Institutional Network", desc: "380+ funds, family offices, banks & NBFCs on-file.", testId: "pillar-network" },
    { icon: FileSignature, label: "Signed Deal Rooms", desc: "Native e-sign, DPDP-grade audit trail, controlled data access.", testId: "pillar-deal-rooms" },
  ];
  return (
    <section className="relative border-t border-b border-navy/[0.06] bg-white" data-testid="hero-pillar-strip">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {pillars.map((p) => {
          const Icon = p.icon;
          return (
            <div key={p.label} className="flex items-start gap-3" data-testid={p.testId}>
              <div className={`w-10 h-10 rounded-lg inline-flex items-center justify-center shrink-0 ${p.proprietary ? "bg-navy text-gold" : "bg-navy/[0.05] text-navy"}`}>
                <Icon size={18} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <div className="font-heading text-sm text-ink leading-tight">{p.label}</div>
                  {p.proprietary && (
                    <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-gold/20 text-[#8A6600] border border-gold/40">
                      Proprietary
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-ink-soft mt-1 leading-relaxed">{p.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ============================== HERO ============================== */
export default function Hero() {
  const videoCfg = useHeroVideoConfig("home");
  const [copy, setCopy] = useState({
    variant_id: "control",
    headline: "Verified private-company intelligence for serious capital.",
    subheadline: "VenturaZ helps founders build a verified capital passport and helps investors discover diligence-backed private opportunities — through company intelligence, readiness scoring, controlled data access, mandate-fit matching, and secure deal rooms.",
    cta_label: "Apply for Funding",
  });
  useEffect(() => {
    let mounted = true;
    fetch(`${API_BASE}/hero-copy`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => { if (mounted && d && d.headline) setCopy(d); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);
  return (
    <>
    <section
      className="relative overflow-hidden liquid-morph-hero !border-0 !shadow-none !rounded-none"
      data-testid="hero-section"
      data-variant={copy.variant_id}
    >
      {/* Concentric rings decoration — top-left */}
      <svg aria-hidden="true" className="absolute -left-24 -top-24 w-[520px] h-[520px] opacity-[0.14]" viewBox="0 0 400 400">
        {[40, 70, 100, 130, 160, 190].map((r) => (
          <circle key={r} cx="200" cy="200" r={r} fill="none" stroke="#FFFFFF" strokeWidth="1" />
        ))}
      </svg>

      {/* Diagonal geometric lines — right side */}
      <svg aria-hidden="true" className="absolute inset-y-0 right-0 h-full w-[62%] opacity-[0.10]" viewBox="0 0 800 700" preserveAspectRatio="none">
        {Array.from({ length: 9 }).map((_, i) => (
          <line
            key={i}
            x1={-200 + i * 140}
            y1={800}
            x2={400 + i * 140}
            y2={-100}
            stroke="#FFFFFF"
            strokeWidth="1.2"
          />
        ))}
      </svg>

      {/* Diamond outline motif — bottom center */}
      <svg aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 bottom-4 w-[280px] h-[80px] opacity-[0.18]" viewBox="0 0 280 80">
        {[0, 40, 80, 120, 160, 200, 240].map((x, i) => (
          <g key={i} transform={`translate(${x} 20) rotate(45 20 20)`}>
            <rect x="0" y="0" width="40" height="40" fill="none" stroke="#FFD700" strokeWidth="1" opacity={0.5 + (i % 3) * 0.15} />
          </g>
        ))}
      </svg>

      {/* Soft vignette to focus content */}
      <div aria-hidden="true" className="absolute inset-0" style={{
        background: "radial-gradient(1200px 700px at 50% 50%, transparent 40%, rgba(15,61,46,0.35) 100%)",
      }} />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 pb-20 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-7 text-cream">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-gold/90" data-testid="hero-eyebrow">
            <ShieldCheck size={12} /> VENTURAZ CAPITAL DISCOVERY PLATFORM
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] mt-4" data-testid="hero-headline">
            {copy.headline}
          </h1>
          <p className="mt-6 text-[15px] sm:text-base leading-relaxed max-w-[640px]" style={{ color: "rgba(246,244,239,0.82)" }} data-testid="hero-subheadline">
            {copy.subheadline}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/startup/apply"
              className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-md font-heading font-semibold text-sm hover:bg-gold/90 transition-colors shadow-[0_10px_30px_rgba(212,175,55,0.25)]"
              data-testid="hero-apply-funding"
            >
              {copy.cta_label} <ArrowRight size={16} />
            </Link>
            <Link
              to="/investor/join"
              className="inline-flex items-center gap-2 bg-white/[0.06] border border-white/15 text-cream px-6 py-3 rounded-md font-heading font-semibold text-sm hover:bg-white/[0.10] transition-colors"
              data-testid="hero-join-investor"
            >
              Join as Investor
            </Link>
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-1 text-cream/80 hover:text-gold text-sm font-mono uppercase tracking-wider transition-colors"
              data-testid="hero-explore-opps"
            >
              Explore Verified Opportunities <ChevronRight size={14} />
            </Link>
            {/* Hero-adjacent watch pill — CMS-driven. When Super Admin
                sets a URL on /admin/hero-pulse the lightbox plays that
                video, otherwise it renders the placeholder poster.
                `enabled: false` hides the pill entirely (creator
                turnaround / temporary takedown). */}
            {videoCfg.enabled !== false && (
              <VideoPreview
                size="thumb"
                palette="navy"
                src={videoCfg.url || undefined}
                title={videoCfg.title}
                description={videoCfg.description}
                duration={videoCfg.duration}
                testId="hero-video-thumb"
              />
            )}
          </div>

          {/* Bottom byline — describes the buyer segments in plain English. */}
          <div className="mt-8 text-[12px] font-mono uppercase tracking-wider" style={{ color: "rgba(246,244,239,0.55)" }}>
            Built for founders raising, investors deploying, family offices, banks & NBFCs — and the corporate-finance teams closing deals for them.
          </div>
        </div>

        {/* Right: Platform Pulse card */}
        <div className="lg:col-span-5 relative min-h-[520px] flex items-center" data-testid="hero-visual">
          <PlatformPulseCard />
        </div>
      </div>
    </section>

    {/* Verified Capital Access pillars — sits right under the hero. */}
    <PillarStrip />
    </>
  );
}
